import { useState, useEffect } from "react";
import api from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Building2, Users, Shield, Key, Plug, Bell, HardDrive, CreditCard, ClipboardList, Palette, Flag, User, Lock, Globe } from "lucide-react";

export default function SettingsPage() {
  const [org, setOrg] = useState(null);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [sso, setSSO] = useState(null);
  const [retention, setRetention] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/settings/org"), api.get("/settings/teams"), api.get("/settings/roles"),
      api.get("/settings/api-keys"), api.get("/settings/sso"), api.get("/settings/retention"),
      api.get("/settings/notifications"), api.get("/settings/feature-flags"),
    ]).then(([o, t, r, k, s, ret, n, f]) => {
      setOrg(o.data); setTeams(t.data); setRoles(r.data); setApiKeys(k.data);
      setSSO(s.data); setRetention(ret.data); setNotifications(n.data); setFeatureFlags(f.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6"><Skeleton className="h-96 bg-white/5" /></div>;

  const Section = ({ title, icon: Icon, children }) => (
    <div className="sf-card p-5">
      <div className="flex items-center gap-2 mb-4"><Icon className="w-4 h-4" style={{ color: "#1E3A5F" }} /><h3 className="text-[14px] font-bold text-[#2D3142]">{title}</h3></div>
      {children}
    </div>
  );

  const Field = ({ label, value }) => (
    <div className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
      <span className="text-[12px]" style={{ color: "#6B7280" }}>{label}</span>
      <span className="text-[12px] font-mono text-[#2D3142]">{value}</span>
    </div>
  );

  return (
    <div className="p-6 space-y-5" data-testid="settings-page">
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Settings</h1>
        <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Organization and platform configuration</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="bg-transparent border-b rounded-none w-full justify-start gap-0 h-auto p-0" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          {[
            { v: "general", l: "General", i: Building2 }, { v: "teams", l: "Teams", i: Users }, { v: "roles", l: "Roles", i: Shield },
            { v: "auth", l: "Auth / SSO", i: Lock }, { v: "keys", l: "API Keys", i: Key }, { v: "notifications", l: "Notifications", i: Bell },
            { v: "retention", l: "Retention", i: HardDrive }, { v: "flags", l: "Feature Flags", i: Flag }, { v: "branding", l: "Branding", i: Palette },
          ].map((t) => (
            <TabsTrigger key={t.v} value={t.v} className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#29B5E8] data-[state=active]:text-[#2D3142] text-[#5A6B82] text-[12px] font-medium px-3 py-2.5 data-[state=active]:bg-transparent" data-testid={`settings-tab-${t.v}`}>
              <t.i className="w-3.5 h-3.5 mr-1.5" />{t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general" className="mt-5 space-y-4">
          <Section title="Organization" icon={Building2}>
            {org && <>{["name", "domain", "plan", "timezone", "data_region"].map((k) => <Field key={k} label={k.replace(/_/g, " ")} value={org[k]} />)}</>}
          </Section>
        </TabsContent>

        <TabsContent value="teams" className="mt-5 space-y-4">
          <Section title="Teams" icon={Users}>
            <div className="space-y-2">
              {teams.map((t) => (
                <div key={t.team_id} className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }} data-testid={`team-${t.team_id}`}>
                  <div><span className="text-[13px] font-medium text-[#2D3142]">{t.name}</span><span className="text-[10px] font-mono ml-2" style={{ color: "#6B7280" }}>{t.slug}</span></div>
                  <div className="flex items-center gap-3"><Badge className="text-[9px] font-mono border-0 rounded" style={{ background: "rgba(30,58,95,0.1)", color: "#1E3A5F" }}>{t.members} members</Badge><span className="text-[10px]" style={{ color: "#6B7280" }}>{t.pipelines?.length} pipelines</span></div>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="roles" className="mt-5 space-y-4">
          <Section title="Roles & Permissions" icon={Shield}>
            <div className="space-y-3">
              {roles.map((r) => (
                <div key={r.role} className="p-3 rounded-lg" style={{ background: "rgba(45,49,66,0.02)", border: "1px solid rgba(255,255,255,0.04)" }} data-testid={`role-${r.role}`}>
                  <div className="flex items-center gap-2 mb-1"><span className="text-[13px] font-bold text-[#2D3142] capitalize">{r.role}</span></div>
                  <p className="text-[11px] mb-2" style={{ color: "#6B7280" }}>{r.description}</p>
                  <div className="flex flex-wrap gap-1">{r.permissions.map((p) => <Badge key={p} className="text-[8px] font-mono border-0 rounded" style={{ background: "rgba(30,58,95,0.08)", color: "#1E3A5F" }}>{p}</Badge>)}</div>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="auth" className="mt-5 space-y-4">
          <Section title="Authentication / SSO" icon={Lock}>
            {sso && <>
              <Field label="SSO Enabled" value={sso.enabled ? "Yes" : "No"} />
              <Field label="Provider" value={sso.provider?.toUpperCase()} />
              <Field label="SCIM Provisioning" value={sso.scim_enabled ? "Enabled" : "Disabled"} />
              <Field label="Enforce SSO" value={sso.enforce_sso ? "Yes" : "No"} />
            </>}
          </Section>
        </TabsContent>

        <TabsContent value="keys" className="mt-5 space-y-4">
          <Section title="API Keys" icon={Key}>
            <div className="space-y-2">
              {apiKeys.map((k) => (
                <div key={k.key_id} className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }} data-testid={`api-key-${k.key_id}`}>
                  <div>
                    <span className="text-[13px] font-medium text-[#2D3142]">{k.name}</span>
                    <span className="text-[10px] font-mono ml-2" style={{ color: "#6B7280" }}>{k.key_preview}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">{k.permissions?.map((p) => <Badge key={p} className="text-[8px] font-mono border-0 rounded" style={{ background: "rgba(30,58,95,0.08)", color: "#1E3A5F" }}>{p}</Badge>)}</div>
                    <Badge className="text-[8px] border-0 rounded" style={{ background: k.is_active ? "rgba(45,106,79,0.12)" : "rgba(190,52,85,0.12)", color: k.is_active ? "#4ADE80" : "#F87171" }}>{k.is_active ? "Active" : "Revoked"}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="notifications" className="mt-5 space-y-4">
          <Section title="Notification Channels" icon={Bell}>
            <div className="space-y-2">
              {notifications?.channels?.map((ch, i) => (
                <div key={i} className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                  <div className="flex items-center gap-2">
                    <Badge className="text-[8px] uppercase font-bold border-0 rounded" style={{ background: "rgba(124,58,237,0.1)", color: "#7C3AED" }}>{ch.type}</Badge>
                    <span className="text-[13px] text-[#2D3142]">{ch.name}</span>
                  </div>
                  <Switch checked={ch.enabled} disabled />
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="retention" className="mt-5 space-y-4">
          <Section title="Data Retention" icon={HardDrive}>
            {retention && Object.entries(retention).map(([k, v]) => <Field key={k} label={k.replace(/_/g, " ")} value={`${v} days`} />)}
          </Section>
        </TabsContent>

        <TabsContent value="flags" className="mt-5 space-y-4">
          <Section title="Feature Flags" icon={Flag}>
            <div className="space-y-2">
              {featureFlags.map((f) => (
                <div key={f.flag} className="flex items-center justify-between py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }} data-testid={`flag-${f.flag}`}>
                  <div><span className="text-[13px] font-medium text-[#2D3142]">{f.flag.replace(/_/g, " ")}</span><p className="text-[10px]" style={{ color: "#6B7280" }}>{f.description}</p></div>
                  <Switch checked={f.enabled} disabled />
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="branding" className="mt-5 space-y-4">
          <Section title="Branding & Appearance" icon={Palette}>
            <Field label="App Name" value="Pipeline Doctor" />
            <Field label="Accent Color" value="#29B5E8" />
            <Field label="Default Theme" value="Dark" />
            <Field label="Logo" value="Custom SVG" />
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
